"use client";

import { Suspense, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { isProtectedPathname, sanitizeInternalNextPath } from "@/lib/auth/protected-routes";
import { getCurrentUser, getSessionRefreshIntervalMs, refresh } from "@/modules/auth/api";
import { useAuthStoreHydrated } from "@/modules/auth/hooks";
import { useAuthStore } from "@/modules/auth/store";

type WorkspaceSessionGateProps = {
  children: ReactNode;
};

/**
 * For cookie-auth + axios bearer to FastAPI: ensures `/api/auth/session` runs and populates
 * `token` before protected pages mount their data hooks (avoids 401 storms and aborted requests
 * when navigation interrupts in-flight calls).
 *
 * `useSearchParams()` must run under Suspense (React Router); see `WorkspaceSessionGate`.
 */
function WorkspaceSessionGateInner({ children }: WorkspaceSessionGateProps) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hydrated = useAuthStoreHydrated();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);

  const pathWithSearch = useMemo(() => {
    const q = searchParams.toString();
    return q ? `${pathname}?${q}` : pathname ?? "";
  }, [pathname, searchParams]);

  const pathWithSearchRef = useRef(pathWithSearch);
  pathWithSearchRef.current = pathWithSearch;

  const protectedRoute = Boolean(pathname && isProtectedPathname(pathname));
  const hasClientSession = Boolean(user && token);
  /** Only after persist rehydration — avoids racing `/api/auth/session` against localStorage. */
  const needsBootstrap = Boolean(protectedRoute && hydrated && !hasClientSession);

  const [blocked, setBlocked] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const refreshInFlightRef = useRef(false);

  useLayoutEffect(() => {
    if (!protectedRoute) {
      setBlocked(false);
      setBootstrapError(null);
      return;
    }
    if (!hydrated) {
      setBlocked(true);
      return;
    }
    if (hasClientSession) {
      setBlocked(false);
      setBootstrapError(null);
      return;
    }
    setBlocked(true);
  }, [protectedRoute, hydrated, hasClientSession]);

  useEffect(() => {
    if (!needsBootstrap) {
      return;
    }

    let cancelled = false;
    const ac = new AbortController();

    (async () => {
      try {
        const sessionUser = await getCurrentUser(ac.signal);
        if (cancelled) {
          return;
        }
        if (!sessionUser) {
          const next = sanitizeInternalNextPath(pathWithSearchRef.current);
          navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
          return;
        }
        setBootstrapError(null);
        setBlocked(false);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setBootstrapError(error instanceof Error ? error.message : "Unable to verify session");
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [needsBootstrap, navigate]);

  useEffect(() => {
    if (!protectedRoute || !hydrated || !hasClientSession) {
      return;
    }

    let cancelled = false;
    const refreshIntervalMs = getSessionRefreshIntervalMs();

    const runHeartbeat = async () => {
      if (cancelled || refreshInFlightRef.current) {
        return;
      }
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return;
      }

      refreshInFlightRef.current = true;
      try {
        const session = await refresh();
        if (cancelled) {
          return;
        }
        if (!session?.user) {
          const next = sanitizeInternalNextPath(pathWithSearchRef.current);
          navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
          return;
        }
        setBootstrapError(null);
      } catch (error) {
        if (cancelled) {
          return;
        }
        useAuthStore.getState().clearSession();
        const next = sanitizeInternalNextPath(pathWithSearchRef.current);
        navigate(`/login?next=${encodeURIComponent(next)}&auth_error=1`, { replace: true });
      } finally {
        refreshInFlightRef.current = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void runHeartbeat();
    }, refreshIntervalMs);

    const handleVisibilityOrFocus = () => {
      void runHeartbeat();
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    }
    window.addEventListener("focus", handleVisibilityOrFocus);
    window.addEventListener("online", handleVisibilityOrFocus);
    void runHeartbeat();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      }
      window.removeEventListener("focus", handleVisibilityOrFocus);
      window.removeEventListener("online", handleVisibilityOrFocus);
    };
  }, [protectedRoute, hydrated, hasClientSession, navigate]);

  if (protectedRoute && blocked) {
    if (bootstrapError) {
      return (
        <div className="flex min-h-[50vh] items-center justify-center px-6 text-center text-caption text-muted">
          {bootstrapError}
        </div>
      );
    }
    return (
      <div className="flex min-h-[50vh] items-center justify-center  text-caption text-muted">
        Loading workspace…
      </div>
    );
  }

  return <>{children}</>;
}

export function WorkspaceSessionGate({ children }: WorkspaceSessionGateProps) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[50vh] items-center justify-center  text-caption text-muted">
          Loading workspace…
        </div>
      }
    >
      <WorkspaceSessionGateInner>{children}</WorkspaceSessionGateInner>
    </Suspense>
  );
}
